import { type JSX, splitProps } from 'solid-js';
import { t } from '../i18n';

// The Agents page's four layout shells (#5627, epic #5620 slice 06): the status strip, the
// full-width Default card, the grid, and one agent card. The LOADED page fills them with
// real controls (`AgentsView`, `AgentCard`, `AgentsStatusStrip`); the LOADING page
// (`AgentsSkeleton`) fills the very same shells with `Skeleton` bars. One set of class
// names, one place, so the skeleton cannot drift from the page it stands in for, and the
// swap to data moves no frame.
//
// Every frame is presentational: no data, no handlers, no state. `decorative` is the
// skeleton's switch: the frame turns `aria-hidden` (and drops the landmark label it would
// otherwise carry), because a screen reader is told about the wait by the live region the
// `QueryBoundary` wraps around the placeholder, not by a row of empty boxes.
//
// None of the frames sets `data-agent`. That attribute names a REAL agent, and the e2e
// specs use `.agent-card[data-agent]` to tell a loaded card from a frame still drawing a
// bar; only `AgentCard` passes it down.

type Decorative = { readonly decorative?: boolean | undefined };

const hidden = (decorative: boolean | undefined): 'true' | undefined =>
  decorative === true ? 'true' : undefined;

export type AgentsStripFrameProps = Decorative & {
  /** The "AI keys" group's content. */
  readonly keys: JSX.Element;
  /** The "Running now" group's content. */
  readonly running: JSX.Element;
};

/** The one hairline status row, two labelled groups. */
export function AgentsStripFrame(props: AgentsStripFrameProps): JSX.Element {
  return (
    <section
      class="agents__strip"
      aria-label={props.decorative === true ? undefined : t('agents.pool.aria')}
      aria-hidden={hidden(props.decorative)}
    >
      <p class="agents__strip-group" data-group="keys">
        {props.keys}
      </p>
      <p class="agents__strip-group" data-group="running">
        {props.running}
      </p>
    </section>
  );
}

export type DefaultCardFrameProps = Decorative & {
  /** Kicker, title and description. */
  readonly head: JSX.Element;
  /** The picker, the advanced fields and the save notice. */
  readonly fields: JSX.Element;
  /** The per-repo overrides block, beside the fields at `md`+. */
  readonly repo: JSX.Element;
};

/** The full-width Default card: the baseline, not an agent. */
export function DefaultCardFrame(props: DefaultCardFrameProps): JSX.Element {
  return (
    <section class="card agents__default" aria-hidden={hidden(props.decorative)}>
      <div class="agents__default-head">{props.head}</div>
      <div class="agents__default-body">
        <div class="agents__default-fields">{props.fields}</div>
        {props.repo}
      </div>
    </section>
  );
}

export type AgentGridFrameProps = Decorative & {
  readonly children: JSX.Element;
};

/** The two-column grid the agent cards sit in. */
export function AgentGridFrame(props: AgentGridFrameProps): JSX.Element {
  return (
    <ul
      class="ui-grid ui-grid--cols-2 ui-grid--gap-md agents__grid"
      aria-label={props.decorative === true ? undefined : t('agents.grid.aria')}
      aria-hidden={hidden(props.decorative)}
    >
      {props.children}
    </ul>
  );
}

export type AgentCardFrameProps = Decorative & {
  /** Title and status pill. */
  readonly head: JSX.Element;
  /** The description and the honesty lines. */
  readonly job: JSX.Element;
  /** The model picker and its save notice. */
  readonly model: JSX.Element;
  /** The status line and the activity line. */
  readonly stats: JSX.Element;
  /** The mode switch, Advanced settings, the changed-by line and the repo chip. */
  readonly foot: JSX.Element;
  /** Anything that follows the five regions: the Advanced drawer. */
  readonly children?: JSX.Element;
  /** Pass-through attributes (`data-*`) for the `li`. */
  readonly [attribute: `data-${string}`]: string | undefined;
};

/** One agent card: five regions, always, in this order (#5623). An empty region keeps its
 * element so the card's row tracks, and the subgrid tracks it shares with its neighbour,
 * never go missing. */
export function AgentCardFrame(props: AgentCardFrameProps): JSX.Element {
  const [own, attributes] = splitProps(props, [
    'head',
    'job',
    'model',
    'stats',
    'foot',
    'children',
    'decorative',
  ]);
  return (
    <li class="card agent-card" aria-hidden={hidden(own.decorative)} {...attributes}>
      <div class="agent-card__head">{own.head}</div>
      <div class="agent-card__job">{own.job}</div>
      <div class="agent-card__model">{own.model}</div>
      <div class="agent-card__stats">{own.stats}</div>
      <div class="agent-card__foot">{own.foot}</div>
      {own.children}
    </li>
  );
}
